<template>
  <div
    v-if="showWidget"
    class="catalog-widget-component"
  >
    <div class="header">
      <dashboard-widget-header
        :icon="widgetIcon"
        :title="title"
      />
      <router-link
        class="view-all-link"
        :to="catalogRoute"
        @click.native="viewAllAnalytics"
      >
        <hlt-text
          :size="'sm'"
          :variant="'content-secondary'"
        >
          View All
        </hlt-text>
      </router-link>
    </div>
    <carousel
      :item-width="296"
      :tile-count="pages.length"
      :tile-type="'catalog'"
    >
      <button
        v-for="page in pages"
        :key="page.id"
        class="page-tile"
        @click="routeToPage(page)"
      >
        <p class="page-section-name">
          {{ catalogPageSectionName(page) }}
        </p>
        <img
          v-if="pageIconUrl(page)"
          class="page-icon"
          :src="pageIconUrl(page)"
        />
        <p class="page-title">
          {{ page.title }}
        </p>
        <p class="page-description">
          {{ stripHtml(page.shortDescription) }}
        </p>
      </button>
    </carousel>
  </div>
</template>

<script>
import Carousel from '@components/carousels/carousel';
import DashboardWidgetHeader from '@/components/dashboard-widgets/header';
import HltText from '@/components/ui/text.vue';

import { stripHtml } from '@/utils';

export default {
  name: 'CatalogWidget',
  components: {Carousel, DashboardWidgetHeader, HltText},
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  computed: {
    allCatalogPages() {
      return this.$store.getters['catalog/pageList'];
    },
    catalog() {
      return this.$store.getters['catalog/get'];
    },
    catalogRoute() {
      return {
        name: 'catalog#index',
      };
    },
    pages() {
      const pages = [];
      if (this.recommendedCatalogPage) {
        pages.push(this.recommendedCatalogPage);
      }
      if (Array.isArray(this.config?.config)) {
        // config.config is in the shape [ { catalogPageId: 1, order: 1 }]
        // Sort by order, find the page, filter out any missing pages, push the page to the pages array
        [...this.config?.config]
          .sort((a, b) => a.order > b.order ? 1 : -1)
          .map(config => this.allCatalogPages?.find(page => page.id === config.catalogPageId))
          .filter(page => page && page.id !== this.recommendedCatalogPage?.id)
          .forEach(page => pages.push(page));
      }
      return pages;
    },
    recommendedCatalogPage() {
      const recPage = this.catalog?.recommendedCatalogPages
        ?.find(rec => rec.classificationId === this.user?.classificationId);
      if (recPage) {
        return this.allCatalogPages?.find(page => page.id === recPage.catalogPageId);
      }
    },
    showWidget() {
      if (!this.user?.catalogPricingEnabled) {
        return false;
      }
      return this.pages && this.pages.length > 0;
    },
    source() {
      return {
        id: this.config?.id,
        name: this.title,
        type: 'Widget',
      };
    },
    title() {
      return this.config?.name || 'Catalog';
    },
    user() {
      return this.$store.getters['user/get'];
    },
    widgetIcon() {
      if (this.config) {
        return this.$store.getters['attachments/get'][this.config.iconAttachmentId];
      }
    },
  },
  created() {
    const attachmentIds = [];
    for (const page of this.pages) {
      if (page.iconId) {
        attachmentIds.push(page.iconId);
      }
    }
    if (attachmentIds.length > 0) {
      this.$store.dispatch('attachments/fetch', { ids: attachmentIds });
    }
  },
  methods: {
    catalogPageSectionName(page) {
      if (page.id === this.recommendedCatalogPage?.id) {
        return 'Recommended Add-on';
      }
      const section = this.catalog?.sections.find(section => page.catalogSectionIds.includes(section.id));
      return section?.title;
    },
    pageIconUrl(page) {
      const attachment = this.$store.getters['attachments/get'][page.iconId];
      return attachment?.url;
    },
    routeToPage(page) {
      this.$router.push({
        name: 'catalog#page#show',
        params: {
          catalogPageId: page.id,
        },
      });
      this.$analytics.events.viewedCatalogPage(page, this.source);
    },
    stripHtml,
    viewAllAnalytics() {
      this.$analytics.events.viewedCatalogHomePage(this.source);
    },
  },
};
</script>

<style lang="scss" scoped>
.catalog-widget-component {
  @include padded-widget;

  .header {
    align-items: baseline;
    display: flex;
    justify-content: space-between;

    .view-all-link {
      color: #767676;
    }
  }

  /deep/ .tile-container {
    height: rem-calc(324);
  }

  .page-tile {
    @include shadowed;

    background-color: #ffffff;
    border-radius: 8px;
    display: inline-block;
    margin: 0 rem-calc(8);
    padding: rem-calc(24) rem-calc(16);
    text-align: center;
    vertical-align: top;
    width: rem-calc(280);
    white-space: initial;

    .page-section-name {
      color: #767676;
      font-size: rem-calc(14);
      font-weight: $bold;
      letter-spacing: 0.54px;
      margin: 0;
      text-transform: uppercase;
    }

    .page-icon {
      height: rem-calc(80);
      margin: rem-calc(24) 0;
      width: rem-calc(80);
    }

    .page-title {
      @include charter-font;

      font-size: rem-calc(24);
      margin: 0;
    }

    .page-description {
      line-height: 1.5;
      height: rem-calc(72);
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}
</style>
